
import { createVertexArray } from "./gl/buffer";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { DEFAULT_ENVIRONMENT, MM_PER_LEGACY_UNIT, type MapEnvironment } from "./mapassets";

/** How dark the corners get, at the corners. */
const VIGNETTE_STRENGTH = 0.42;

export function ambientFor(environment: MapEnvironment): readonly [number, number, number] {
  const [r, g, b] = environment.directionalLight.background.diffuse;
  return [r, g, b];
}

export const DEFAULT_SLOPE_FLOOR: readonly [number, number, number] = [0.6, 0.6, 0.6];

export function slopeFloorFor(environment: MapEnvironment): readonly [number, number, number] {
  const { emissive, ambient, diffuse } = environment.material;
  const light = environment.directionalLight.background;
  const floor: [number, number, number] = [0.6, 0.6, 0.6];
  for (let channel = 0; channel < 3; channel += 1) {
    const shaded = emissive[channel] + ambient[channel] * light.ambient[channel];
    // Saturated, because the fixed-function pipeline clamps the vertex colour
    // it hands the texture stage: a1's lit end is 1.32 and reaches the screen
    // as 1.0.
    const lit = Math.min(1, shaded + diffuse[channel] * light.diffuse[channel]);
    floor[channel] = lit > 0 ? Math.min(1, Math.max(0, shaded / lit)) : 0;
  }
  return floor;
}

export function fogFor(environment: MapEnvironment): {
  readonly nearMm: number;
  readonly farMm: number;
  readonly colour: readonly [number, number, number];
  readonly enabled: boolean;
} {
  const [r, g, b] = environment.fog.colour;
  return {
    nearMm: environment.fog.nearDistance * MM_PER_LEGACY_UNIT,
    farMm: environment.fog.farDistance * MM_PER_LEGACY_UNIT,
    colour: [r, g, b],
    enabled: environment.fog.enable,
  };
}

const VERTEX_SOURCE = `#version 300 es
precision highp float;

out vec2 v_screen;

void main() {
  // One triangle covering the viewport, generated from the vertex id: no buffer,
  // no attributes, and no seam down the middle the way two triangles have.
  vec2 corner = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  v_screen = corner;
  gl_Position = vec4(corner * 2.0 - 1.0, 0.0, 1.0);
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_screen;

uniform vec3 u_ambient;
uniform float u_strength;
uniform float u_aspect;

out vec4 fragColour;

void main() {
  vec2 offset = (v_screen - 0.5) * vec2(u_aspect, 1.0);
  // Falls off from well outside the centre, so the middle third of the screen --
  // where the player is -- is untouched and only the periphery sinks.
  float centre = smoothstep(0.82, 0.30, length(offset));
  fragColour = vec4(u_ambient * mix(1.0 - u_strength, 1.0, centre), 1.0);
}
`;

const UNIFORMS = ["u_ambient", "u_strength", "u_aspect"] as const;

export class Atmosphere {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private environment: MapEnvironment = DEFAULT_ENVIRONMENT;
  private nightEnvironment: MapEnvironment | null = null;
  private night = false;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "atmosphere");
    this.uniforms = uniformLocations(gl, this.program, UNIFORMS);
    // The draw has no attributes, but a VAO still has to be bound: WebGL2 refuses
    // to draw with the default one when it is left unbound after another pass.
    this.vao = createVertexArray(gl, "atmosphere");
  }

  setNight(night: boolean): void {
    this.night = night;
  }

  /** The map's record and the one the night flag selects, from `createRenderer`
   *  before the first frame. */
  setEnvironment(environment: MapEnvironment, night: MapEnvironment | null): void {
    this.environment = environment;
    this.nightEnvironment = night;
  }

  /** Which record this frame is lit by. The night record only wins when the
   *  server sent the flag *and* the pack shipped one to swap in. */
  get active(): MapEnvironment {
    return this.night && this.nightEnvironment !== null ? this.nightEnvironment : this.environment;
  }

  draw(widthPx: number, heightPx: number): void {
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    const ambient = ambientFor(this.active);
    gl.uniform3f(this.uniforms.u_ambient, ambient[0], ambient[1], ambient[2]);
    gl.uniform1f(this.uniforms.u_strength, VIGNETTE_STRENGTH);
    gl.uniform1f(this.uniforms.u_aspect, heightPx > 0 ? widthPx / heightPx : 1);

    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ZERO, gl.SRC_COLOR);
    gl.disable(gl.DEPTH_TEST);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }
}
